import { useRef, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { FORM_URLS } from '@/utils/formUrl';

export default function AboutPage() {
  const [contactStatus, setContactStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [contactError, setContactError] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const jsonLdOrg = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '国产在线',
    url: 'https://guochanzx.com',
    description: '国产在线精选内容平台，汇聚海量优质国产视频资源',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-9999',
      contactType: 'customer service',
      email: 'contact@guochanzx.com',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      streetAddress: '建国路88号',
      addressCountry: 'CN',
    },
  };

  const jsonLdFaq = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '国产在线平台是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '国产在线是国内领先的精选内容聚合平台，汇聚海量优质国产影视、综艺、动漫、纪录片等内容资源，为用户提供高清流畅的在线观看体验，无需下载，免费访问。',
        },
      },
      {
        '@type': 'Question',
        name: '国产在线平台覆盖哪些内容类型？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '国产在线覆盖五大核心内容领域：国产剧、国产电影、国产综艺、国产动漫和国产纪录片。平台持续更新，涵盖古装、现代、悬疑、都市、科幻、美食、人文等多种细分题材。',
        },
      },
      {
        '@type': 'Question',
        name: '使用国产在线需要付费吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '国产在线平台完全免费，无需注册即可访问所有内容。我们致力于为用户提供极致的国产在线视听体验，让每一位用户都能轻松享受优质内容。',
        },
      },
      {
        '@type': 'Question',
        name: '国产在线平台的内容多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '国产在线平台每日更新，编辑团队实时追踪最新最热的国产内容资源，确保用户能第一时间获取到最新最全的国产在线内容信息。',
        },
      },
    ],
  };

  const jsonLdPage = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 国产在线',
    description: '了解国产在线平台的品牌故事、平台优势和联系方式',
    url: 'https://guochanzx.com/about',
  };

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubscribing(true);
    setContactStatus('idle');
    setContactError('');

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setContactStatus('success');
      form.reset();
      setSubscribing(false);
      return;
    }

    try {
      const formData = new FormData(form);
      if (honeypotEl) {
        formData.delete('phone_alt');
      }

      const submitUrl = FORM_URLS['联系我们'];
      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setContactStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setContactError('提交失败，请稍后再试。');
          setContactStatus('error');
        } else {
          setContactError(serverMsg || '提交失败，请稍后再试。');
          setContactStatus('error');
        }
      }
    } catch {
      setContactError('网络错误，请检查网络连接后重试。');
      setContactStatus('error');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLdOrg)}</script>
      <script type="application/ld+json">{JSON.stringify(jsonLdFaq)}</script>
      <script type="application/ld+json">{JSON.stringify(jsonLdPage)}</script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '关于我们' }]} />

            <section className="mt-6 mb-12">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
                关于我们
              </h1>
              <p className="text-sm text-foreground-500 mt-2 leading-relaxed">
                国产在线是国内领先的精选内容聚合平台，致力于为用户提供最优质的国产在线视听体验。
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-lg md:text-xl font-bold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                品牌介绍
              </h2>
              <div className="bg-white rounded-lg border border-background-200 p-6">
                <div className="prose max-w-none text-foreground-700 leading-relaxed text-sm">
                  <p>
                    国产在线成立于2020年，是国内领先的<strong>国产内容精选平台</strong>。我们汇聚了海量优质国产内容资源，涵盖<strong>国产剧、国产电影、国产综艺、国产动漫、国产纪录片</strong>等多元题材。
                  </p>
                  <p>
                    平台采用先进的内容筛选和推荐技术，每日更新最新最热的国产在线资源。无需下载即可<strong>高清流畅播放</strong>，为用户打造极致的在线观看体验。
                  </p>
                  <p>
                    我们的编辑团队由资深的影视从业者和内容爱好者组成，以专业的眼光精选每一部作品，确保平台内容的品质和水准。无论你是国产剧迷、电影爱好者还是综艺追更党，都能在国产在线找到属于自己的精彩内容。
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-lg md:text-xl font-bold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { name: '李先生', text: '国产在线是我每天必刷的平台，内容更新快，分类清晰，找剧特别方便！', rating: 5 },
                  { name: '王女士', text: '平台内容质量很高，每篇推荐都很用心，已经推荐给身边很多朋友了。', rating: 5 },
                  { name: '张同学', text: '喜欢这里的国产动漫板块，国漫资源很全，更新速度也很快。', rating: 5 },
                  { name: '陈先生', text: '免费就能看到这么多优质内容，太良心了！国产在线真的很好用。', rating: 5 },
                ].map((review, i) => (
                  <div key={i} className="bg-white rounded-lg border border-background-200 p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                        <span className="text-accent-700 font-semibold text-sm">{review.name[0]}</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-800">{review.name}</p>
                        <div className="flex gap-0.5 mt-0.5">
                          {Array.from({ length: review.rating }).map((_, j) => (
                            <i key={j} className="ri-star-fill text-xs text-accent-500"></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{review.text}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-lg md:text-xl font-bold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                平台优势
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { icon: 'ri-shield-check-line', title: '品质保障', desc: '专业编辑团队精心筛选，每部作品都经过严格品控' },
                  { icon: 'ri-flashlight-line', title: '极速更新', desc: '每日持续更新，第一时间获取最新国产在线内容资源' },
                  { icon: 'ri-eye-line', title: '免费观看', desc: '无需注册付费，高清流畅播放，畅享极致视听体验' },
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-lg border border-background-200 p-5 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-50 text-primary-600">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-2">{item.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-lg md:text-xl font-bold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                常见问题
              </h2>
              <div className="space-y-3">
                {[
                  { q: '国产在线平台是什么？', a: '国产在线是国内领先的精选内容聚合平台，汇聚海量优质国产影视、综艺、动漫、纪录片等内容资源，为用户提供高清流畅的在线观看体验，无需下载，免费访问。' },
                  { q: '国产在线平台覆盖哪些内容类型？', a: '国产在线覆盖五大核心内容领域：国产剧、国产电影、国产综艺、国产动漫和国产纪录片。平台持续更新，涵盖古装、现代、悬疑、都市、科幻、美食、人文等多种细分题材。' },
                  { q: '使用国产在线需要付费吗？', a: '国产在线平台完全免费，无需注册即可访问所有内容。我们致力于为用户提供极致的国产在线视听体验，让每一位用户都能轻松享受优质内容。' },
                  { q: '国产在线平台的内容多久更新一次？', a: '国产在线平台每日更新，编辑团队实时追踪最新最热的国产内容资源，确保用户能第一时间获取到最新最全的国产在线内容信息。' },
                ].map((faq, i) => (
                  <details key={i} className="bg-white rounded-lg border border-background-200 group">
                    <summary className="p-4 text-sm font-medium text-foreground-800 cursor-pointer flex items-center justify-between">
                      {faq.q}
                      <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-400"></i>
                    </summary>
                    <p className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </details>
                ))}
              </div>
            </section>

            <section className="mb-6">
              <h2 className="text-lg md:text-xl font-bold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg border border-background-200 p-6">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-4">联系方式</h3>
                  <ul className="space-y-3 text-sm text-foreground-600">
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-50 text-accent-600 flex-shrink-0">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <p className="font-medium text-foreground-800">电子邮箱</p>
                        <p>contact@guochanzx.com</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-50 text-accent-600 flex-shrink-0">
                        <i className="ri-phone-line"></i>
                      </div>
                      <div>
                        <p className="font-medium text-foreground-800">客服电话</p>
                        <p>400-888-9999</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-50 text-accent-600 flex-shrink-0">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      <div>
                        <p className="font-medium text-foreground-800">公司地址</p>
                        <p>北京市朝阳区建国路88号</p>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg border border-background-200 p-6">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-4">在线留言</h3>
                  <form ref={formRef} onSubmit={handleContactSubmit} data-readdy-form="">
                    <div className="space-y-3">
                      <div>
                        <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-600 mb-1">姓名</label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-600 mb-1">邮箱</label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-600 mb-1">留言内容</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={3}
                          className="w-full px-3 py-2 text-sm border border-background-300 rounded-md bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                          placeholder="请输入您的留言内容（500字以内）"
                        ></textarea>
                      </div>
                      <input
                        type="text"
                        name="phone_alt"
                        className="phone-alt-field"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                      <button
                        type="submit"
                        disabled={subscribing}
                        className="w-full py-2.5 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {subscribing ? '提交中...' : '提交留言'}
                      </button>
                    </div>
                    {contactStatus === 'success' && (
                      <p className="mt-3 text-sm text-green-600">提交成功！感谢您的留言，我们会尽快回复。</p>
                    )}
                    {contactStatus === 'error' && (
                      <p className="mt-3 text-sm text-red-500">{contactError}</p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}